import { Typography } from '@mui/material';
import { Stack } from '@mui/system';
import React, { useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTracking } from '../../../../Tracking/TrackingContext';
import {
  WaitApplicationResponse
} from '../../../../clients/LoanClient';
import BrazeContentCard from '../../../../components/BrazeContentCard/BrazeContentCard';
import Loading from '../../../../components/Loading/Loading';
import { useSnackBar } from '../../../../contexts/SnackBarContext';
import useCustomNavigate from '../../../../hooks/Navigation';
import useLoanClient from '../../../../hooks/loan/Client';
import { determineJourneyPath, navigateToAffordabilityChallenge, navigateToDocumentUpload, navigateToTermsAndConditions } from '../../../../utils/Helpers/NavigationHelper';
import { PostProcessingRoutes, RouterRoutes } from '../../../../utils/RouterRoutes';

const AffordabilityChallengeProcessing: React.FC = () => {
  const loanClient = useLoanClient();
  const { displaySnackBar } = useSnackBar();
  const location = useLocation();
  const loanId = location.state["loanId"] ?? "";
  const { isExistingCustomer } = useTracking();
  const isConcurrentLoan = location.pathname.includes(RouterRoutes.concurrentJourney);
  const { navigateWithGclidParams } = useCustomNavigate();

  const waitApplication = useCallback(async (loanId: string) => {
    let response: WaitApplicationResponse | undefined;
    for (let i = 0; i < 10; i++) {
      response =
        await loanClient.waitApplication(
          loanId,
          30,
          true,
          false,
          false,
          false,
          true,
          false,
          false
        );

      if (response.success || response.applicationInfo?.isDeclined) break;
    }
    return response;
  }, [loanClient]);

  const handleDecision = (waitApplicationResponse: WaitApplicationResponse, loanId: string) => {
    const isRequireSign = waitApplicationResponse?.applicationAdvice?.requireSign;
    const isRequireDocuments = waitApplicationResponse?.applicationAdvice?.requireDocuments;
    const isDeclined = waitApplicationResponse?.applicationInfo?.isDeclined;
    const isAffordabilityChallenge = waitApplicationResponse?.applicationAdvice?.affordabilityChallenge;
    const success = waitApplicationResponse?.success;
    // Added !isDeclined to this because when the application is declined, success is returned as false
    const isTimedOut = !success && !isDeclined;
    const counterOfferAvailable = waitApplicationResponse?.applicationAdvice?.counterOffer;
    if (isRequireSign) {
      navigateToTermsAndConditions(navigateWithGclidParams, loanId ?? '', isExistingCustomer, isConcurrentLoan, true);
    } else if (isRequireDocuments) {
      navigateToDocumentUpload(navigateWithGclidParams, loanId ?? '', isExistingCustomer, isConcurrentLoan, true);
    } else if (isDeclined) {
      const route = determineJourneyPath(isExistingCustomer, isConcurrentLoan);
      navigateWithGclidParams(`/${route}/${PostProcessingRoutes.declined}`, { replace: true, state: { loanId } });
    } else if (counterOfferAvailable) {
      const route = determineJourneyPath(isExistingCustomer, isConcurrentLoan);
      navigateWithGclidParams(`/${route}/${PostProcessingRoutes.counterOffer}`, { replace: true });
    } else if (isAffordabilityChallenge) {
      navigateToAffordabilityChallenge(navigateWithGclidParams, loanId ?? '', isExistingCustomer, isConcurrentLoan, true);
    } else if (isTimedOut) {
      navigateWithGclidParams(`/${RouterRoutes.myLoan}/${loanId}`, { replace: true });
    }
  };


  const processAffordabilityChallenge = async () => {
    try {
      const waitApplicationResponse = await waitApplication(loanId);

      // eslint-disable-next-line
      handleDecision(waitApplicationResponse!, loanId);
    } catch (error) {
      displaySnackBar("Oops! An unexpected error has occurred.", "error");
    }
  };

  useEffect(() => {
    processAffordabilityChallenge();

    // eslint-disable-next-line
  }, []);

  return (
    <>
      <BrazeContentCard customPageFeed={PostProcessingRoutes.affordabilityChallengeProcessing} />
      <Stack flexGrow={1} alignItems="center" padding={"4rem 2rem 4rem 2rem"}>
        <Typography variant="h2" textAlign={"center"}>We are processing your application</Typography>
        <Loading />
        <Typography variant="body1" marginTop={2}>
          Please note that we may reach out to you to confirm your changes.
        </Typography>
      </Stack>
    </>
  );
};

export default AffordabilityChallengeProcessing;
